import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, User, Bot, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = ["Peaceful", "Anxious", "Curious", "Restless", "Grateful", "Confused", "Energized", "Tired"];
const GOALS = ["Inner peace", "Cosmic consciousness", "Healing", "Self-knowledge", "Energy awakening", "Focus", "Spiritual growth", "Overcoming fear"];

const QUICK_PROMPTS = [
  "What practice should I start with today?",
  "Guide me on the path to cosmic consciousness",
  "How can I open my third eye?",
  "What is the fastest way to enlightenment?",
  "Help me choose a meditation technique",
  "How do I awaken my Kundalini safely?",
];

export default function SpiritualGuidePage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mood, setMood] = useState("");
  const [goal, setGoal] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/guide/history`).then((r) => {
      setMessages(r.data);
      setLoadingHistory(false);
    }).catch(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;
    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/guide/chat`, {
        message: text,
        mood: mood || null,
        goal: goal || null,
      });
      const aiMsg = { role: "assistant", content: data.response, timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (e) {
      const errorDetail = e.response?.data?.detail || "Failed to get response";
      toast.error(typeof errorDetail === 'string' ? errorDetail : "AI guide temporarily unavailable");
      // Remove the user message if AI failed
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/guide/history`);
      setMessages([]);
      toast("Chat history cleared");
    } catch {
      toast.error("Failed to clear history");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <main className="min-h-screen flex flex-col" data-testid="spiritual-guide-page">
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col px-6 md:px-12 pt-6 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-1">
              AI Spiritual Guide
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-light text-[#1A221E]">
              Your Personal Guide
            </h1>
            <p className="text-sm text-[#737E79] mt-1">
              Powered by GPT-5.2 with knowledge of all spiritual traditions
            </p>
          </div>
          <button
            onClick={clearHistory}
            data-testid="clear-chat-btn"
            className="p-2 rounded-full text-[#737E79] hover:bg-[#EAE2D6] hover:text-[#1A221E] transition-all duration-300"
            title="Clear chat history"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Mood & Goal selectors */}
        <div className="flex flex-wrap gap-2 mb-4">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="mood-selector"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E3DDD2] bg-white text-xs text-[#737E79] hover:bg-[#EAE2D6] transition-colors"
            >
              {mood ? `Mood: ${mood}` : "Set Mood"}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-xl">
              <DropdownMenuItem onClick={() => setMood("")} className="cursor-pointer text-xs">Clear</DropdownMenuItem>
              {MOODS.map((m) => (
                <DropdownMenuItem key={m} data-testid={`mood-${m.toLowerCase()}`} onClick={() => setMood(m)} className="cursor-pointer text-xs">
                  {m}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="goal-selector"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E3DDD2] bg-white text-xs text-[#737E79] hover:bg-[#EAE2D6] transition-colors"
            >
              {goal ? `Goal: ${goal}` : "Set Goal"}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-xl">
              <DropdownMenuItem onClick={() => setGoal("")} className="cursor-pointer text-xs">Clear</DropdownMenuItem>
              {GOALS.map((g) => (
                <DropdownMenuItem key={g} data-testid={`goal-${g.toLowerCase().replace(/\s/g, '-')}`} onClick={() => setGoal(g)} className="cursor-pointer text-xs">
                  {g}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {mood && <Badge className="bg-[#8FA69A]/10 text-[#2C4A3B] border-0 rounded-full text-xs">{mood}</Badge>}
          {goal && <Badge className="bg-[#D4AF37]/10 text-[#8B7520] border-0 rounded-full text-xs">{goal}</Badge>}
        </div>

        {/* Chat area */}
        <div className="flex-1 min-h-0 rounded-2xl bg-white border border-[#E3DDD2] flex flex-col overflow-hidden">
          <div className="flex-1 p-6 overflow-y-auto" ref={scrollRef} style={{ maxHeight: "calc(100vh - 380px)" }}>
            <div className="space-y-4">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8" data-testid="chat-loading">
                  <Loader2 className="w-6 h-6 animate-spin text-[#8FA69A]" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12" data-testid="chat-empty">
                  <Sparkles className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-medium text-[#1A221E] mb-2">
                    Welcome, {user?.name || "Seeker"}
                  </h3>
                  <p className="text-sm text-[#737E79] mb-6 max-w-sm mx-auto">
                    Ask me about any spiritual practice, technique, or path. I'm here to guide your journey to cosmic consciousness.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
                    {QUICK_PROMPTS.map((prompt) => (
                      <button
                        key={prompt}
                        data-testid={`quick-prompt-${prompt.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                        onClick={() => sendMessage(prompt)}
                        className="px-3 py-1.5 rounded-full border border-[#E3DDD2] text-xs text-[#737E79] hover:bg-[#EAE2D6] hover:text-[#1A221E] transition-all duration-300"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={i}
                    data-testid={`chat-message-${msg.role}-${i}`}
                    className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                        <Bot className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-[#8FA69A] text-white rounded-br-md"
                          : "bg-[#F0EDE6] text-[#1A221E] rounded-bl-md"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === "user" && (
                      <div className="w-8 h-8 rounded-full bg-[#8FA69A]/10 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-[#8FA69A]" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                ))
              )}
              {sending && (
                <div className="flex gap-3" data-testid="ai-thinking">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-[#F0EDE6] flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#8FA69A]" />
                    <span className="text-sm text-[#737E79]">Contemplating...</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-[#E3DDD2] flex gap-3" data-testid="chat-input-form">
            <Input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your spiritual guide..."
              disabled={sending}
              className="flex-1 rounded-full bg-[#F9F6F0] border-[#E3DDD2] focus:ring-[#8FA69A] focus:border-[#8FA69A] text-sm"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="send-message-btn"
              className="w-10 h-10 rounded-full bg-[#8FA69A] text-white flex items-center justify-center hover:bg-[#2C4A3B] transition-all duration-300 disabled:opacity-50 shrink-0"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
